import { IBreadcrumbItem } from "@fluentui/react/lib/Breadcrumb";
import { Location } from "history";

/**
 * Parameters required to build Manage Projects breadcrumb
 */
export interface ManageProjectsBreadcrumbParams {
    customerTitle: string;
    appName: string;
    location: Location;
    history: any;
}

/**
 * Builds breadcrumb items for Manage Projects App
 */
export const buildManageProjectsBreadcrumb = (
    params: ManageProjectsBreadcrumbParams
): IBreadcrumbItem[] => {

    const { customerTitle, appName, location, history } = params;
    const items: IBreadcrumbItem[] = [];

    // --------------------------------------------------------
    // ROOT ITEMS
    // --------------------------------------------------------
    items.push({
        text: 'ZedX Apps',
        key: "root",
        onClick: () => history.push("/")
    });

    items.push({
        text: appName,
        key: "app",
        onClick: () => history.push("/a2/welcome")
    });

    // --------------------------------------------------------
    // DETERMINE CURRENT PATH
    // (supports BrowserRouter & HashRouter)
    // --------------------------------------------------------
    let path = location.pathname || "";
    if ((!path || path === "/") && location.hash) {
        path = location.hash
            .replace(/^#/, "")
            .split("?")[0]
            .split("#")[0];
    }

    // --------------------------------------------------------
    // ROUTE-SPECIFIC BREADCRUMB ITEMS
    // --------------------------------------------------------

    if (path === "/a2/welcome") {
        items.push({ text: "Welcome", key: "welcome", isCurrentItem: true });
    }
    else if (path === "/a2/insights") {
        items.push({ text: "Insights", key: "insights", isCurrentItem: true });
    }
    else if (path === "/a2/ai-chats") {
        items.push({ text: "Cloe AI Chats", key: "cloe-ai-chats", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/ask-cloe")) {
        items.push({
            text: "Cloe AI Chats",
            key: "cloe-ai-chats",
            onClick: () => history.push('/a2/ai-chats')
        });
        items.push({ text: "Ask Cloe", key: "ask-cloe", isCurrentItem: true });
    }

    // ---------------- VISIONS ----------------
    else if (path === "/a2/visions") {
        items.push({ text: "Visions", key: "visions", isCurrentItem: true });
    }

    // ---------------- OBJECTIVES ----------------
    else if (path === "/a2/objectives") {
        items.push({ text: "Objectives", key: "objectives", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/objectives-for-vision")) {
        items.push({ text: "Objectives", key: "objectives", isCurrentItem: true });
    }

    // ---------------- PORTFOLIOS ----------------
    else if (path === "/a2/portfolios") {
        items.push({ text: "Portfolios", key: "portfolios", isCurrentItem: true });
    }

    // ---------------- FINANCIALS: PORTFOLIOS ----------------
    else if (path === "/a2/financials/portfolios") {
        items.push({ text: "Financials", key: "financials", isCurrentItem: true });
        items.push({ text: "Portfolios", key: "portfolios-financials", isCurrentItem: true });
    }

    else if (path.startsWith("/a2/portfolios-for-objective")) {
        items.push({ text: "Portfolios", key: "portfolios", isCurrentItem: true });
    }

    // ---------------- PORTFOLIO UPDATES & SIGN-OFF ----------------
    else if (path.startsWith("/a2/updates-for-portfolio")) {
        items.push({ text: "Portfolio Updates", key: "portfolio-updates", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/sign-off-for-portfolio")) {
        items.push({ text: "Portfolio Sign Off", key: "portfolio-signoff", isCurrentItem: true });
    }

    // ---------------- DIRECTORATES ----------------
    else if (path === "/a2/directorates") {
        items.push({ text: "Directorates", key: "directorates", isCurrentItem: true });
    }

    // ---------------- PROGRAMMES ----------------
    else if (path === "/a2/programmes") {
        items.push({ text: "Programmes", key: "programmes", isCurrentItem: true });
    }

    // ---------------- FINANCIALS: PROGRAMMES ----------------
    else if (path === "/a2/financials/programmes") {
        items.push({ text: "Financials", key: "financials", isCurrentItem: true });
        items.push({ text: "Programmes", key: "programmes-financials", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/financials/programmes-for-portfolio")) {
        items.push({ text: "Financials", key: "financials", isCurrentItem: true });
        items.push({ text: "Programmes", key: "programmes-financials", isCurrentItem: true });
    }

    else if (path.startsWith("/a2/programmes-for-portfolio")) {
        items.push({ text: "Programmes", key: "programmes", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/updates-for-programme")) {
        items.push({ text: "Programme Updates", key: "programme-updates", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/sign-off-for-programme")) {
        items.push({ text: "Programme Sign Off", key: "programme-signoff", isCurrentItem: true });
    }

    // ---------------- PROJECTS ----------------
    else if (path === "/a2/projects") {
        items.push({ text: "Projects", key: "projects", isCurrentItem: true });
    }

    // ---------------- FINANCIALS: PROJECTS ----------------
    else if (path === "/a2/financials/projects") {
        items.push({ text: "Financials", key: "financials", isCurrentItem: true });
        items.push({ text: "Projects", key: "projects-financials", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/financials/projects-for-portfolio")
        || path.startsWith("/a2/financials/projects-for-programme")) {
        items.push({ text: "Financials", key: "financials", isCurrentItem: true });
        items.push({ text: "Projects", key: "projects-financials", isCurrentItem: true });
    }

    else if (path.startsWith("/a2/projects-for-portfolio")
        || path.startsWith("/a2/projects-for-programme")
        || path.startsWith("/a2/projects-for-directorate")) {
        items.push({ text: "Projects", key: "projects", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/updates-for-project")) {
        items.push({ text: "Project Updates", key: "project-updates", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/sign-off-for-project")) {
        items.push({ text: "Project Sign Off", key: "project-signoff", isCurrentItem: true });
    }

    // ---------------- KEY WORK AREAS ----------------
    else if (path === "/a2/key-work-areas") {
        items.push({ text: "Key Work Areas", key: "key-work-areas", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/key-work-areas-for-directorate")) {
        items.push({ text: "Key Work Areas", key: "key-work-areas", isCurrentItem: true });
    }

    // ---------------- RISKS ----------------
    else if (path.startsWith("/a2/risks-for-project")) {
        items.push({ text: "Risks", key: "risks", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/updates-for-risk")) {
        items.push({ text: "Risk Updates", key: "risk-updates", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/sign-off-for-risk")) {
        items.push({ text: "Risk Sign Off", key: "risk-signoff", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/risk-mitigating-actions-for-risk")) {
        items.push({ text: "Risk Mitigation Actions", key: "risk-mitigations", isCurrentItem: true });
    }

    // ---------------- WORK STREAMS ----------------
    else if (path === "/a2/work-streams") {
        items.push({ text: "Work Streams", key: "work-streams", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/work-streams-for-project")) {
        items.push({ text: "Work Streams", key: "work-streams", isCurrentItem: true });
    }

    // ---------------- METRICS ----------------
    else if (path === "/a2/metrics") {
        items.push({ text: "Metrics", key: "metrics", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/metrics-for-directorate")) {
        items.push({ text: "Metrics", key: "metrics", isCurrentItem: true });
    }

    // ---------------- MILESTONES ----------------
    else if (path === "/a2/milestones") {
        items.push({ text: "Milestones", key: "milestones", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/milestones-for-work-stream")
        || path.startsWith("/a2/milestones-for-work-area")) {
        items.push({ text: "Milestones", key: "milestones", isCurrentItem: true });
    }

    // ---------------- BENEFITS ----------------
    else if (path === "/a2/benefits") {
        items.push({ text: "Benefits", key: "benefits", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/benefits-for-project")) {
        items.push({ text: "Benefits", key: "benefits", isCurrentItem: true });
    }

    // ---------------- COMMITMENTS ----------------
    else if (path === "/a2/commitments") {
        items.push({ text: "Commitments", key: "commitments", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/commitments-for-directorate")) {
        items.push({ text: "Commitments", key: "commitments", isCurrentItem: true });
    }

    // ---------------- DEPENDENCIES ----------------
    else if (path === "/a2/dependencies") {
        items.push({ text: "Dependencies", key: "dependencies", isCurrentItem: true });
    }
    else if (path.startsWith("/a2/dependencies-for-project")) {
        items.push({ text: "Dependencies", key: "dependencies", isCurrentItem: true });
    }

    // ---------------- OUTPUTS ----------------
    else if (path === "/a2/outputs") {
        items.push({ text: "Outputs", key: "outputs", isCurrentItem: true });
    }

    // ---------------- SETTINGS ----------------
    else if (path.startsWith("/a2/settings")) {
        const segments = path.split("/");
        const lastSegment = segments[segments.length - 1];
        items.push({ text: "Settings", key: "settings", isCurrentItem: true });
        items.push({ text: lastSegment.replace(/-/g, " "), key: lastSegment, isCurrentItem: true });
    }

    return items;
};